import jsPDF from "jspdf";
import "jspdf-autotable";

const BLACK = [20, 20, 20];
const LABEL_FILL = [225, 235, 242];
const BORDER = [120, 120, 120];
const MARGIN = 34;
const clean = (value) => String(value ?? "").trim();
const choice = (selected, label) => `[${selected ? "X" : " "}] ${label}`;

function sectionTitle(doc, title, y) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(...BLACK);
  doc.text(title, MARGIN + 4, y);
  return y + 8;
}

function addPageFooter(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.5);
  doc.setTextColor(...BLACK);
  doc.text(
    "Modello verbale verifica finale - test scritto",
    pageWidth - MARGIN,
    pageHeight - 12,
    {
      align: "right",
    },
  );
}

function tableDefaults() {
  return {
    theme: "grid",
    margin: { left: MARGIN, right: MARGIN, bottom: 25 },
    styles: {
      font: "helvetica",
      fontSize: 7.7,
      textColor: BLACK,
      lineColor: BORDER,
      lineWidth: 0.35,
      cellPadding: { top: 3, right: 5, bottom: 3, left: 5 },
      valign: "middle",
      overflow: "linebreak",
    },
  };
}

function metadataCellStyles() {
  return {
    0: { cellWidth: 132, fillColor: LABEL_FILL, fontStyle: "bold" },
    1: { cellWidth: 280 },
    2: { cellWidth: 155, fillColor: LABEL_FILL, fontStyle: "bold" },
    3: { cellWidth: "auto" },
  };
}

function participantResult(participant, threshold) {
  return participant.minimumAttendance === "yes" &&
    Number(participant.score) >= Number(threshold)
    ? "suitable"
    : "not-suitable";
}

function drawMainHeader(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("VERBALE DELLA VERIFICA FINALE", pageWidth / 2, 31, {
    align: "center",
  });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.text(
    "per il conseguimento dell'attestato di formazione",
    pageWidth / 2,
    46,
    {
      align: "center",
    },
  );
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8.2);
  doc.text(
    "Modello predisposto per verifica finale tramite test scritto",
    pageWidth / 2,
    58,
    {
      align: "center",
    },
  );
}

export function generateVerbaleVerificaFinalePdf(data, signatureDataUrl) {
  const doc = new jsPDF("landscape", "pt", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - MARGIN * 2;
  drawMainHeader(doc);

  let y = sectionTitle(doc, "1. Dati del corso e soggetto formatore", 72);
  doc.autoTable({
    ...tableDefaults(),
    startY: y,
    body: [
      [
        "Titolo corso:",
        clean(data.courseTitle),
        "Soggetto formatore:",
        clean(data.trainingProvider),
      ],
      [
        "Riferimento normativo:",
        clean(data.regulatoryReference),
        "Sede legale / operativa:",
        clean(data.courseLocation),
      ],
      [
        "Durata e periodo:",
        clean(data.durationAndPeriod),
        "Responsabile progetto formativo:",
        clean(data.projectManager),
      ],
      [
        "Modalità di erogazione:",
        clean(data.deliveryMode),
        "Docente/i:",
        clean(data.teachers),
      ],
      [
        "Sede / piattaforma:",
        clean(data.platform),
        "Note:",
        clean(data.courseNotes),
      ],
    ],
    columnStyles: metadataCellStyles(),
  });

  y = sectionTitle(
    doc,
    "2. Verifica finale di apprendimento",
    doc.lastAutoTable.finalY + 11,
  );
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.2);
  const intro = "La verifica finale di apprendimento è stata svolta mediante ";
  doc.text(intro, MARGIN + 4, y + 3);
  const introWidth = doc.getTextWidth(intro);
  doc.setFont("helvetica", "bold");
  doc.text("test scritto.", MARGIN + 5 + introWidth, y + 3);
  y += 9;

  const testDescription = `${choice(data.testType === "multiple", "risposta multipla")}  ${choice(data.testType === "true-false", "vero/falso")}  ${choice(data.testType === "other", "altro")}`;
  doc.autoTable({
    ...tableDefaults(),
    startY: y,
    body: [
      [
        "Data verifica finale:",
        clean(data.testDate),
        "Luogo / modalità verifica:",
        clean(data.testLocation),
      ],
      [
        "Numero domande / soglia:",
        `${clean(data.questionCount)} domande - ${clean(data.threshold)}/${clean(data.questionCount)}`,
        "Tipologia:",
        testDescription,
      ],
    ],
    columnStyles: {
      0: { cellWidth: 140, fillColor: LABEL_FILL, fontStyle: "bold" },
      1: { cellWidth: 280 },
      2: { cellWidth: 150, fillColor: LABEL_FILL, fontStyle: "bold" },
      3: { cellWidth: "auto" },
    },
  });

  y = sectionTitle(
    doc,
    "3. Elenco partecipanti ammessi alla verifica finale ed esito",
    doc.lastAutoTable.finalY + 11,
  );
  const participantRows = data.participants.map((participant, index) => {
    const result = participantResult(participant, data.threshold);
    return [
      index + 1,
      clean(participant.name),
      `${choice(participant.minimumAttendance === "yes", "Sì")}   ${choice(participant.minimumAttendance === "no", "No")}`,
      `${clean(participant.score)} / ${clean(data.questionCount)}`,
      `${choice(result === "suitable", "Idoneo")}   ${choice(result === "not-suitable", "Non idoneo")}`,
      clean(participant.notes),
    ];
  });
  while (participantRows.length < 13) {
    participantRows.push([participantRows.length + 1, "", "", "", "", ""]);
  }

  doc.autoTable({
    ...tableDefaults(),
    startY: y,
    margin: { top: 45, left: MARGIN, right: MARGIN, bottom: 25 },
    head: [
      [
        "N.",
        "Identificativo partecipante / Cognome e Nome",
        "Presenza minima raggiunta",
        "Punteggio test",
        "Esito",
        "Note",
      ],
    ],
    body: participantRows,
    styles: {
      ...tableDefaults().styles,
      cellPadding: { top: 1.5, right: 5, bottom: 1.5, left: 5 },
      fontSize: 7.5,
      minCellHeight: 12,
    },
    headStyles: {
      fillColor: LABEL_FILL,
      textColor: BLACK,
      fontStyle: "bold",
      halign: "center",
    },
    columnStyles: {
      0: { cellWidth: 30, halign: "center" },
      1: { cellWidth: 275 },
      2: { cellWidth: 125, halign: "center" },
      3: { cellWidth: 92, halign: "center" },
      4: { cellWidth: 125, halign: "center" },
      5: { cellWidth: "auto" },
    },
    showHead: "everyPage",
    willDrawPage: (hookData) => {
      if (hookData.pageNumber > 1) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text("3. Elenco partecipanti - continuazione", MARGIN, 31);
      }
    },
  });

  y = doc.lastAutoTable.finalY + 11;
  const declaration =
    "Il Responsabile del progetto formativo dichiara che i partecipanti sopra indicati sono stati ammessi alla verifica finale secondo i criteri previsti dal progetto formativo e che la verifica di apprendimento è stata effettuata mediante test scritto. Gli esiti della verifica sono riportati nel presente verbale e conservati nel fascicolo del corso, unitamente alla documentazione prevista: progetto formativo, programma, registro presenze, test somministrati, griglie di correzione, elenco docenti e attestati rilasciati. Il modello è predisposto in coerenza con l'Accordo Stato-Regioni 17/04/2025, Rep. atti n. 59/CSR, con particolare riferimento alla documentazione delle verifiche finali.";
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.2);
  const declarationLines = doc.splitTextToSize(declaration, contentWidth - 8);
  const declarationHeight = declarationLines.length * 8.2;
  if (y + declarationHeight + 56 > doc.internal.pageSize.getHeight() - 24) {
    doc.addPage("a4", "landscape");
    y = 31;
  }
  y = sectionTitle(doc, "4. Dichiarazione finale", y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.2);
  doc.text(declarationLines, MARGIN + 4, y + 1, { lineHeightFactor: 1.08 });
  y += declarationHeight + 4;

  doc.autoTable({
    ...tableDefaults(),
    startY: y,
    body: [
      ["Luogo:", clean(data.documentPlace), "Data:", clean(data.testDate)],
      ["Firma del Responsabile del progetto formativo:", "", "", ""],
    ],
    columnStyles: metadataCellStyles(),
    didParseCell: (hookData) => {
      if (hookData.section === "body" && hookData.row.index === 1) {
        hookData.cell.styles.minCellHeight = 26;
        if (hookData.column.index >= 2)
          hookData.cell.styles.fillColor = [255, 255, 255];
      }
    },
    didDrawCell: (hookData) => {
      if (
        signatureDataUrl &&
        hookData.section === "body" &&
        hookData.row.index === 1 &&
        hookData.column.index === 1
      ) {
        doc.addImage(
          signatureDataUrl,
          "PNG",
          hookData.cell.x + 86,
          hookData.cell.y + 1,
          60,
          30,
        );
      }
    },
  });
  y = doc.lastAutoTable.finalY + 8;
  const privacy =
    "Per il principio di minimizzazione dei dati, nel verbale è preferibile evitare dati anagrafici non necessari, come luogo di nascita, data di nascita, residenza o altri dati eccedenti. La tracciabilità può essere garantita mediante nome e cognome oppure mediante identificativo interno collegato al registro presenze e al fascicolo corso.";
  const privacyLines = doc.splitTextToSize(privacy, contentWidth - 10);
  const privacyHeight = privacyLines.length * 8.4 + 17;
  if (y + privacyHeight > doc.internal.pageSize.getHeight() - 24) {
    doc.addPage("a4", "landscape");
    y = 42;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.text("Nota privacy operativa", MARGIN + 5, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text(privacyLines, MARGIN + 5, y + 13, {
    lineHeightFactor: 1.15,
  });
  addPageFooter(doc);
  return doc;
}
